import React, { useEffect, useState } from 'react';
import axios from 'axios';
import SellerLayout from './SellerLayout';

const SellerNoticeList = () => {
  const [loading, setLoading] = useState(true);

  const [msg, setMsg] = useState('');
  const [q, setQ] = useState('');
  const [searchText, setSearchText] = useState('');

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [notices, setNotices] = useState([]);
  const [sortValues, setSortValues] = useState({});

  const fetchData = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams(window.location.search);
      const currentQ = params.get('q') || '';
      const currentPage = parseInt(params.get('page') || '1', 10) || 1;

      const res = await axios.get(
        `/api/seller/notices?q=${encodeURIComponent(currentQ)}&page=${currentPage}`,
        { withCredentials: true }
      );

      if (res.data && res.data.ok) {
        const rows = Array.isArray(res.data.notices) ? res.data.notices : [];

        const initialSorts = {};
        rows.forEach((row) => {
          initialSorts[row.id] = row.sort_order ?? 0;
        });

        setMsg(res.data.msg || '');
        setQ(res.data.q || '');
        setSearchText(res.data.q || '');

        setPage(Number(res.data.page || 1));
        setPerPage(Number(res.data.per_page || 20));
        setTotal(Number(res.data.total || 0));
        setTotalPages(Number(res.data.total_pages || 1));

        setNotices(rows);
        setSortValues(initialSorts);
      } else if (res.data && res.data.redirect) {
        window.location.href = res.data.redirect;
      }
    } catch (err) {
      if (err.response && err.response.status === 401) {
        window.location.href =
          err.response.data?.redirect || '/seller/manage';
        return;
      }

      console.error('셀러 공지 목록 실패:', err);
      setNotices([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [window.location.search]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    window.location.href = `/seller/notices?q=${encodeURIComponent(searchText)}&page=1`;
  };

  const handleSortChange = (id, value) => {
    setSortValues((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const postAction = async (url, payload) => {
    try {
      const res = await axios.post(url, payload, {
        withCredentials: true,
      });

      if (res.data && res.data.ok) {
        fetchData();
      } else if (res.data && res.data.redirect) {
        window.location.href = res.data.redirect;
      }
    } catch (err) {
      if (err.response && err.response.status === 401) {
        window.location.href =
          err.response.data?.redirect || '/seller/manage';
        return;
      }

      alert(
        err.response?.data?.error ||
        err.response?.data?.msg ||
        '처리 중 오류가 발생했습니다.'
      );
    }
  };

  const handleSaveSort = (id) => {
    postAction('/api/seller/notices/sort', {
      id,
      sort_order: Number(sortValues[id] ?? 0),
    });
  };

  const handleToggle = (id) => {
    const ok = window.confirm('노출 상태를 변경할까요?');
    if (!ok) return;

    postAction('/api/seller/notices/toggle', { id });
  };

  const handleDelete = (id) => {
    const ok = window.confirm('정말 삭제할까요?');
    if (!ok) return;

    postAction('/api/seller/notices/delete', { id });
  };

  const formatCreatedAt = (value) => {
    if (!value) return '';
    return String(value).slice(0, 10);
  };

  const prev = page > 1 ? page - 1 : 1;
  const next = page < totalPages ? page + 1 : totalPages;
  const start = page - 2 > 1 ? page - 2 : 1;
  const end = page + 2 < totalPages ? page + 2 : totalPages;

  return (
    <SellerLayout>

      <div className="container my-4">

        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">셀러 공지사항</h4>
          <a href="/seller/notices/add" className="btn btn-dark btn-sm">
            + 공지 등록
          </a>
        </div>

        {msg && (
          <div className="alert alert-success py-2">{msg}</div>
        )}

        <form className="mb-3" onSubmit={handleSearchSubmit}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="제목 검색"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <button className="btn btn-outline-dark" type="submit">
              검색
            </button>
          </div>
        </form>

        <div className="card shadow-sm">
          <div className="card-body p-0">
            <table className="table table-hover mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '80px' }}>ID</th>
                  <th>제목</th>
                  <th className="text-center" style={{ width: '110px' }}>노출</th>
                  <th className="text-center" style={{ width: '160px' }}>정렬</th>
                  <th className="text-center" style={{ width: '160px' }}>작성일</th>
                  <th className="text-center" style={{ width: '260px' }}>관리</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      불러오는 중...
                    </td>
                  </tr>
                ) : total <= 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      등록된 공지사항이 없습니다.
                    </td>
                  </tr>
                ) : (
                  notices.map((row) => (
                    <tr key={row.id}>
                      <td>{row.id}</td>
                      <td className="fw-semibold">{row.title}</td>

                      <td className="text-center">
                        {parseInt(row.is_active, 10) === 1 ? (
                          <span className="badge bg-success">노출</span>
                        ) : (
                          <span className="badge bg-secondary">숨김</span>
                        )}
                      </td>

                      <td className="text-center">
                        <form
                          className="d-inline-flex gap-2"
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleSaveSort(row.id);
                          }}
                        >
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={sortValues[row.id] ?? 0}
                            onChange={(e) =>
                              handleSortChange(row.id, e.target.value)
                            }
                            style={{ width: '90px' }}
                          />
                          <button
                            className="btn btn-sm btn-outline-dark"
                            type="submit"
                          >
                            저장
                          </button>
                        </form>
                      </td>

                      <td className="text-center">
                        {formatCreatedAt(row.created_at)}
                      </td>

                      <td className="text-center">
                        <a
                          href={`/seller/notices/edit?id=${row.id}`}
                          className="btn btn-sm btn-outline-primary me-2"
                        >
                          수정
                        </a>

                        <button
                          className="btn btn-sm btn-outline-warning me-2"
                          onClick={() => handleToggle(row.id)}
                        >
                          노출토글
                        </button>

                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(row.id)}
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {!loading && totalPages > 1 && (
          <nav className="mt-3">
            <ul className="pagination justify-content-center">
              <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                <a
                  className="page-link"
                  href={`/seller/notices?q=${encodeURIComponent(q)}&page=${prev}`}
                >
                  이전
                </a>
              </li>

              {Array.from(
                { length: end - start + 1 },
                (_, i) => start + i
              ).map((i) => (
                <li
                  key={i}
                  className={`page-item ${i === page ? 'active' : ''}`}
                >
                  <a
                    className="page-link"
                    href={`/seller/notices?q=${encodeURIComponent(q)}&page=${i}`}
                  >
                    {i}
                  </a>
                </li>
              ))}

              <li
                className={`page-item ${
                  page >= totalPages ? 'disabled' : ''
                }`}
              >
                <a
                  className="page-link"
                  href={`/seller/notices?q=${encodeURIComponent(q)}&page=${next}`}
                >
                  다음
                </a>
              </li>
            </ul>
          </nav>
        )}
      </div>
    </SellerLayout>
  );
};

export default SellerNoticeList;